import { useRef, useState } from 'react';
import { useKaydedilmemisIcerik } from '../shell/KaydedilmemisIcerik';
import {
  Alert,
  Anchor,
  Button,
  Checkbox,
  FileButton,
  Group,
  List,
  Modal,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core';
import type {
  ContractorDocumentDetail,
  ContractorPersonelSatiri,
  LookupItemDetail,
  SahaGirisi,
} from '@isg/shared-types';
import {
  AKSIYON_EK_LIMIT,
  DOSYA_MAX_BOYUT_BYTES,
  EVRAK_EKSENI_ETIKETI,
  EVRAK_ONAY_DURUMU_ETIKETI,
  sahaGirisiNedenMetni,
  readEvrakTipiKurali,
} from '@isg/validation';
import {
  useEvrakAc,
  useEvrakEkIndir,
  useEvrakEkSil,
  useEvrakEkYukle,
  useEvrakEkleri,
  useEvrakKarari,
  useMyAuthorization,
  useYukleniciEkipmani,
  useSahaGunu,
} from '../api/queries';
import { gunGirdisiIso, tarihGoster } from '../lib/format';
import { QueryStatus } from './parts';
import { Rozet, SatirEylemleri, SolukSatir } from '../sozlesme';
import { ANLAM, type RozetAnlami } from '../theme';

/**
 * YÜKLENİCİ EVRAK YÜZEYİ — detay sayfası ve portal PAYLAŞIR (F1.5·S12
 * deseni; **SERTLEŞTİRME TUR 6** yeniden yazımı). İki ekran aynı kuralı
 * kopyalamaz: yetki bayrakları, ön kontrol uyarıları ve saha girişi
 * SUNUCUDAN gelir.
 *
 * - Karar düğmeleri `yetkiler.kararVerebilir`den (K4 — istemci rol listesi
 *   YOK); temsilcide sunucu false döner, düğme çizilmez.
 * - Yükleme SOLUK SATIRLA (P1-a): tip → eksen (tipin kuralından) → kişi /
 *   ekipman seçimi → tarihler → dosya. Kayıt + dosya iki adımdır (önce
 *   kayıt, sonra ek); ikisi de sunucu kapısından geçer.
 * - Vade rozeti gösterim eşiği motor varsayılanıyla (30 gün) aynıdır;
 *   SAHA GİRİŞİ kararı bu rozetten DEĞİL sunucudan okunur (§2.40).
 */

export function SahaGirisiRozeti({
  sahaGirisi,
  testId = 'saha-girisi-rozeti',
}: {
  sahaGirisi: SahaGirisi;
  testId?: string;
}): React.JSX.Element {
  return (
    <Rozet anlam={sahaGirisi.gecerli ? 'olumlu' : 'engel'} testId={testId}>
      {sahaGirisi.gecerli ? 'Saha girişi geçerli' : 'Saha girişi geçersiz'}
    </Rozet>
  );
}

/** Nedenler — rozetin YANINDA (§2.40: sebep görünür). */
export function SahaGirisiNedenleri({
  sahaGirisi,
}: {
  sahaGirisi: SahaGirisi;
}): React.JSX.Element | null {
  if (sahaGirisi.nedenler.length === 0) return null;
  return (
    <List size="sm" data-testid="saha-girisi-nedenleri">
      {sahaGirisi.nedenler.map((neden, i) => (
        <List.Item key={i}>
          {neden.evrakTipiAd}
          {neden.eksen === 'kisi' ? ' (kişi)' : ''} — {sahaGirisiNedenMetni(neden)}
        </List.Item>
      ))}
    </List>
  );
}

/** Vade rozeti SUNUCUDAN türer (`vadeDurumu`) — ekran eşik yazmaz, tarih kıyaslamaz (§2.33 deseni). */
function vadeRozeti(doc: ContractorDocumentDetail): {
  anlam: RozetAnlami;
  etiket: string;
  testId: string;
} {
  switch (doc.vadeDurumu) {
    case 'suresiz':
      return { anlam: 'bilgi', etiket: 'Vadesiz', testId: 'evrak-vadesiz' };
    case 'dolmus':
      return { anlam: 'engel', etiket: 'Süresi doldu', testId: 'evrak-dolmus' };
    case 'yaklasiyor':
      return { anlam: 'dikkat', etiket: 'Yaklaşıyor', testId: 'evrak-yaklasiyor' };
    case 'gecerli':
      return { anlam: 'olumlu', etiket: 'Geçerli', testId: 'evrak-gecerli' };
  }
}

function durumRozeti(doc: ContractorDocumentDetail): { anlam: RozetAnlami; testId: string } {
  if (doc.onayDurumu === 'onaylandi') return { anlam: 'olumlu', testId: 'evrak-onayli' };
  if (doc.onayDurumu === 'reddedildi') return { anlam: 'engel', testId: 'evrak-reddedildi' };
  return { anlam: 'dikkat', testId: 'evrak-bekliyor' };
}

/**
 * EVRAK TABLOSU — satır: tip · bağlı kişi/ekipman · geçerlilik · durum ·
 * dosyalar · eylemler. Soluk satır (yükleme) listenin sonunda (P1).
 */
export function EvrakTablosu({
  contractorId,
  documents,
  evrakTipleri,
  personel,
  yukleyebilir,
  portal = false,
}: {
  contractorId: string;
  documents: ContractorDocumentDetail[];
  evrakTipleri: LookupItemDetail[] | undefined;
  personel: ContractorPersonelSatiri[] | undefined;
  yukleyebilir: boolean;
  portal?: boolean;
}): React.JSX.Element {
  const karar = useEvrakKarari();
  const [redEvrak, setRedEvrak] = useState<ContractorDocumentDetail | null>(null);
  const [acikEkler, setAcikEkler] = useState<string | null>(null);
  const KOLON = 7;

  return (
    <Stack gap="xs">
      {karar.isError && (
        <Alert color={ANLAM.engel} role="alert" data-testid="evrak-karar-hata">
          {karar.error.message}
        </Alert>
      )}
      {redEvrak && (
        <RedModali
          evrak={redEvrak}
          onClose={() => setRedEvrak(null)}
          onRed={(gerekce, hassasVeri) =>
            karar.mutate(
              {
                contractorId,
                documentId: redEvrak.id,
                karar: { sonuc: 'reddedildi', gerekce, hassasVeri },
              },
              { onSuccess: () => setRedEvrak(null) },
            )
          }
          kaydediliyor={karar.isPending}
        />
      )}
      <Table data-testid="evrak-listesi">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Evrak</Table.Th>
            <Table.Th>Bağlı</Table.Th>
            <Table.Th>Geçerlilik</Table.Th>
            <Table.Th>Vade</Table.Th>
            <Table.Th>Durum</Table.Th>
            <Table.Th>Dosya</Table.Th>
            <Table.Th />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {documents.map((doc) => {
            const vade = vadeRozeti(doc);
            const durum = durumRozeti(doc);
            const eklerAcik = acikEkler === doc.id;
            return (
              <EvrakSatiri
                key={doc.id}
                doc={doc}
                vade={vade}
                durum={durum}
                portal={portal}
                eklerAcik={eklerAcik}
                onEklerToggle={() => setAcikEkler(eklerAcik ? null : doc.id)}
                onOnayla={() =>
                  karar.mutate({ contractorId, documentId: doc.id, karar: { sonuc: 'onaylandi' } })
                }
                onReddet={() => setRedEvrak(doc)}
                kolonSayisi={KOLON}
                contractorId={contractorId}
              />
            );
          })}
          {yukleyebilir && (
            <EvrakSolukSatiri
              contractorId={contractorId}
              evrakTipleri={evrakTipleri}
              personel={personel}
              kolonSayisi={KOLON - 1}
              onAcildi={(id) => setAcikEkler(id)}
            />
          )}
        </Table.Tbody>
      </Table>
      {documents.length === 0 && !yukleyebilir && (
        <Text size="sm" c="dimmed" data-testid="evrak-yok">
          Kayıtlı evrak yok.
        </Text>
      )}
    </Stack>
  );
}

function EvrakSatiri({
  doc,
  vade,
  durum,
  portal,
  eklerAcik,
  onEklerToggle,
  onOnayla,
  onReddet,
  kolonSayisi,
  contractorId,
}: {
  doc: ContractorDocumentDetail;
  vade: { anlam: RozetAnlami; etiket: string; testId: string };
  durum: { anlam: RozetAnlami; testId: string };
  portal: boolean;
  eklerAcik: boolean;
  onEklerToggle: () => void;
  onOnayla: () => void;
  onReddet: () => void;
  kolonSayisi: number;
  contractorId: string;
}): React.JSX.Element {
  const bagli = doc.personAd ?? doc.equipmentAd ?? 'Firma';
  return (
    <>
      <Table.Tr data-testid="evrak-satiri" data-evrak-id={doc.id}>
        <Table.Td>
          <Text size="sm" fw={500}>
            {doc.evrakTipiAd}
          </Text>
          <Text size="xs" c="dimmed">
            {EVRAK_EKSENI_ETIKETI[doc.eksen]}
            {doc.zorunlu ? ' · zorunlu' : ''}
            {doc.saglik ? ' · sağlık' : ''}
            {doc.oncekiSurumId ? ' · yeni sürüm' : ''}
          </Text>
        </Table.Td>
        <Table.Td>{bagli}</Table.Td>
        <Table.Td>
          {doc.gecerlilikBaslangic ? tarihGoster(doc.gecerlilikBaslangic) : '—'} –{' '}
          {doc.gecerlilikBitis ? tarihGoster(doc.gecerlilikBitis) : 'vadesiz'}
        </Table.Td>
        <Table.Td>
          <Rozet anlam={vade.anlam} testId={vade.testId}>
            {vade.etiket}
          </Rozet>
        </Table.Td>
        <Table.Td>
          <Rozet anlam={durum.anlam} testId={durum.testId}>
            {EVRAK_ONAY_DURUMU_ETIKETI[doc.onayDurumu]}
          </Rozet>
          {doc.redGerekce && (
            <Text size="xs" c="dimmed" data-testid="evrak-red-gerekcesi">
              {doc.redGerekce}
            </Text>
          )}
        </Table.Td>
        <Table.Td>
          <Anchor size="sm" onClick={onEklerToggle} data-testid="evrak-ekler-ac">
            {doc.ekSayisi} dosya
          </Anchor>
        </Table.Td>
        <Table.Td>
          <SatirEylemleri
            birincil={
              doc.yetkiler.kararVerebilir
                ? { etiket: 'Onayla', onClick: onOnayla, testId: 'evrak-onayla' }
                : undefined
            }
            digerleri={[
              {
                etiket: 'Reddet',
                onClick: onReddet,
                gizli: !doc.yetkiler.kararVerebilir,
                tehlikeli: true,
                testId: 'evrak-reddet',
              },
            ]}
            testId="evrak-eylemleri"
          />
        </Table.Td>
      </Table.Tr>
      {doc.onKontrolUyarilari.length > 0 && !portal && (
        <Table.Tr data-testid="evrak-on-kontrol">
          <Table.Td colSpan={kolonSayisi}>
            <Alert color={ANLAM.dikkat} variant="light" title="Ön kontrol">
              <List size="sm">
                {doc.onKontrolUyarilari.map((u) => (
                  <List.Item key={u}>{u}</List.Item>
                ))}
              </List>
            </Alert>
          </Table.Td>
        </Table.Tr>
      )}
      {eklerAcik && (
        <Table.Tr data-testid="evrak-ekler">
          <Table.Td colSpan={kolonSayisi}>
            <EvrakEkleri contractorId={contractorId} doc={doc} />
          </Table.Td>
        </Table.Tr>
      )}
    </>
  );
}

/** Dosyalar — aksiyon eki deseni: yükle (yetkiliyse) · indir · sil (kendi dosyası, kararsız evrak). */
function EvrakEkleri({
  contractorId,
  doc,
}: {
  contractorId: string;
  doc: ContractorDocumentDetail;
}): React.JSX.Element {
  const ekler = useEvrakEkleri(doc.id);
  const yukle = useEvrakEkYukle();
  const sil = useEvrakEkSil();
  const indir = useEvrakEkIndir();
  const authz = useMyAuthorization();
  const sifirla = useRef<() => void>(null);
  const [yerelHata, setYerelHata] = useState<string | null>(null);
  const doluluk = ekler.data?.length ?? 0;
  return (
    <Stack gap="xs" data-testid="evrak-ek-bolumu">
      <Group justify="space-between">
        <Text size="sm" fw={500}>
          Dosyalar ({doluluk}/{AKSIYON_EK_LIMIT})
        </Text>
        {doc.yetkiler.ekYukleyebilir && (
          <FileButton
            resetRef={sifirla}
            accept=".jpg,.jpeg,.png,.heic,.pdf"
            onChange={(dosya) => {
              if (!dosya) return;
              setYerelHata(null);
              if (dosya.size > DOSYA_MAX_BOYUT_BYTES) {
                setYerelHata('Dosya 10 MB sınırını aşıyor.');
                sifirla.current?.();
                return;
              }
              yukle.mutate(
                { contractorId, documentId: doc.id, dosya },
                { onSettled: () => sifirla.current?.() },
              );
            }}
          >
            {(props) => (
              <Button
                {...props}
                size="xs"
                variant="light"
                data-testid="evrak-ek-yukle"
                loading={yukle.isPending}
              >
                Dosya ekle
              </Button>
            )}
          </FileButton>
        )}
      </Group>
      <QueryStatus queries={[ekler]} />
      {(yerelHata ??
        (yukle.isError ? yukle.error.message : null) ??
        (sil.isError ? sil.error.message : null)) && (
        <Text size="sm" c={ANLAM.engel} role="alert" data-testid="evrak-ek-hata">
          {yerelHata ?? yukle.error?.message ?? sil.error?.message}
        </Text>
      )}
      {ekler.isSuccess && ekler.data.length === 0 && (
        <Text size="sm" c="dimmed" data-testid="evrak-ek-yok">
          Henüz dosya yok.
        </Text>
      )}
      {ekler.isSuccess && ekler.data.length > 0 && (
        <Table data-testid="evrak-ek-listesi">
          <Table.Tbody>
            {ekler.data.map((ek) => (
              <Table.Tr key={ek.id} data-testid="evrak-ek-satiri">
                <Table.Td>
                  {ek.indirilebilir ? (
                    <Anchor
                      size="sm"
                      data-testid="evrak-ek-indir"
                      onClick={() =>
                        indir.mutate({
                          documentId: doc.id,
                          attachmentId: ek.id,
                          dosyaAdi: ek.dosyaAdi,
                        })
                      }
                    >
                      {ek.dosyaAdi}
                    </Anchor>
                  ) : (
                    <Text size="sm" data-testid="evrak-ek-kilitli">
                      {ek.dosyaAdi}
                    </Text>
                  )}
                </Table.Td>
                <Table.Td>
                  <Text size="xs" c="dimmed">
                    {ek.yukleyenEtiket} · {tarihGoster(ek.createdAt)}
                  </Text>
                </Table.Td>
                <Table.Td>
                  {doc.onayDurumu === 'yuklendi' &&
                    ek.yukleyenAppUserId === authz.data?.appUserId && (
                      <Button
                        size="xs"
                        variant="subtle"
                        color={ANLAM.engel}
                        data-testid="evrak-ek-sil"
                        loading={sil.isPending}
                        onClick={() =>
                          sil.mutate({ contractorId, documentId: doc.id, attachmentId: ek.id })
                        }
                      >
                        Sil
                      </Button>
                    )}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Stack>
  );
}

/**
 * SOLUK SATIR — evrak kaydı + (isteğe bağlı) dosya. Tip seçilince EKSEN tipin
 * kuralından okunur ve kişi/ekipman seçici ancak o eksende çizilir (§2.40 —
 * kural istemiyorsa alan yok). Vadesiz tipte bitiş alanı çizilmez.
 */
function EvrakSolukSatiri({
  contractorId,
  evrakTipleri,
  personel,
  kolonSayisi,
  onAcildi,
}: {
  contractorId: string;
  evrakTipleri: LookupItemDetail[] | undefined;
  personel: ContractorPersonelSatiri[] | undefined;
  kolonSayisi: number;
  onAcildi: (documentId: string) => void;
}): React.JSX.Element {
  const ac = useEvrakAc();
  const yukle = useEvrakEkYukle();
  // Madde 302: yer tutucudaki örnek gün SAHA GÜNÜ (sunucudan).
  const sahaGunu = useSahaGunu();
  const ekipman = useYukleniciEkipmani(contractorId);
  const [tipId, setTipId] = useState<string | null>(null);
  const [personId, setPersonId] = useState<string | null>(null);
  const [equipmentId, setEquipmentId] = useState<string | null>(null);
  const [baslangic, setBaslangic] = useState('');
  const [bitis, setBitis] = useState('');
  const [dosya, setDosya] = useState<File | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const tip = evrakTipleri?.find((t) => t.id === tipId) ?? null;
  const kural = tip ? readEvrakTipiKurali(tip.kurallar) : null;
  const dokunuldu = tipId !== null || baslangic !== '' || bitis !== '';
  // P12 (madde 224) — kaydedilmemiş evrak satırı çıkışta uyarır (yönlendirici tek noktada).
  useKaydedilmemisIcerik(dokunuldu ? 1 : 0, 'evrak satırı');
  const bitisIso = bitis ? gunGirdisiIso(bitis) : null;
  const baslangicIso = baslangic ? gunGirdisiIso(baslangic) : null;
  const kaydedilebilir =
    tipId !== null &&
    (kural?.eksen !== 'kisi' || personId !== null) &&
    (kural?.eksen !== 'ekipman' || equipmentId !== null) &&
    (!kural?.vadeTakibi || bitisIso !== null) &&
    (baslangic === '' || baslangicIso !== null);

  const kaydet = (): void => {
    if (!kaydedilebilir || !tipId) return;
    setHata(null);
    ac.mutate(
      {
        contractorId,
        input: {
          evrakTipiId: tipId,
          personId: kural?.eksen === 'kisi' ? personId : null,
          equipmentId: kural?.eksen === 'ekipman' ? equipmentId : null,
          gecerlilikBaslangic: baslangicIso ? baslangic : null,
          gecerlilikBitis: kural?.vadeTakibi && bitisIso ? bitis : null,
        },
      },
      {
        onSuccess: (evrak) => {
          if (dosya) {
            yukle.mutate(
              { contractorId, documentId: evrak.id, dosya },
              { onError: (e) => setHata(`Kayıt açıldı, dosya yüklenemedi: ${e.message}`) },
            );
          }
          onAcildi(evrak.id);
          setTipId(null);
          setPersonId(null);
          setEquipmentId(null);
          setBaslangic('');
          setBitis('');
          setDosya(null);
        },
        onError: (e) => setHata(e.message),
      },
    );
  };

  return (
    <>
      <SolukSatir
        testId="evrak-soluk-satir"
        dokunuldu={dokunuldu}
        kaydedilebilir={kaydedilebilir}
        kaydediliyor={ac.isPending || yukle.isPending}
        onKaydet={kaydet}
        kolonSayisi={kolonSayisi}
        ikinciKatman={
          hata ? (
            <Text size="sm" c={ANLAM.engel} role="alert" data-testid="evrak-ac-hata">
              {hata}
            </Text>
          ) : undefined
        }
      >
        <Table.Td>
          <Select
            size="xs"
            placeholder="Evrak tipi"
            data-testid="evrak-tip"
            searchable
            comboboxProps={{ withinPortal: true }}
            data={(evrakTipleri ?? [])
              .filter((t) => !t.gizli)
              .map((t) => ({ value: t.id, label: t.ad }))}
            value={tipId}
            onChange={(deger) => {
              setTipId(deger);
              setPersonId(null);
              setEquipmentId(null);
            }}
          />
        </Table.Td>
        <Table.Td>
          {kural?.eksen === 'kisi' && (
            <Select
              size="xs"
              placeholder="Kişi"
              data-testid="evrak-kisi"
              searchable
              comboboxProps={{ withinPortal: true }}
              data={(personel ?? []).map((p) => ({ value: p.personId, label: p.adSoyad }))}
              value={personId}
              onChange={setPersonId}
            />
          )}
          {kural?.eksen === 'ekipman' && (
            <Select
              size="xs"
              placeholder="Ekipman"
              data-testid="evrak-ekipman"
              searchable
              comboboxProps={{ withinPortal: true }}
              data={(ekipman.data ?? []).map((e) => ({ value: e.id, label: e.ad }))}
              value={equipmentId}
              onChange={setEquipmentId}
            />
          )}
          {kural?.eksen === 'firma' && (
            <Text size="xs" c="dimmed">
              Firma
            </Text>
          )}
        </Table.Td>
        <Table.Td>
          <Group gap={4} wrap="nowrap">
            <TextInput
              size="xs"
              placeholder="Başlangıç"
              type="date"
              data-testid="evrak-baslangic"
              value={baslangic}
              onChange={(e) => setBaslangic(e.currentTarget.value)}
              w={110}
            />
            {(kural === null || kural.vadeTakibi) && (
              <TextInput
                size="xs"
                placeholder={sahaGunu.data ? `Bitiş (${sahaGunu.data})` : 'Bitiş'}
                type="date"
                data-testid="evrak-bitis"
                value={bitis}
                onChange={(e) => setBitis(e.currentTarget.value)}
                w={130}
              />
            )}
          </Group>
        </Table.Td>
        <Table.Td>
          <Text size="xs" c="dimmed">
            {kural ? (kural.vadeTakibi ? 'vadeli' : 'vadesiz') : '—'}
          </Text>
        </Table.Td>
        <Table.Td>
          <Text size="xs" c="dimmed">
            yüklendi
          </Text>
        </Table.Td>
        <Table.Td>
          <FileButton accept=".jpg,.jpeg,.png,.heic,.pdf" onChange={setDosya}>
            {(props) => (
              <Button {...props} size="xs" variant="subtle" data-testid="evrak-dosya-sec">
                {dosya ? dosya.name : 'Dosya seç'}
              </Button>
            )}
          </FileButton>
        </Table.Td>
      </SolukSatir>
    </>
  );
}

/** Red — gerekçe ZORUNLU (madde 49 ruhu) + hassas veri işareti (madde 41.4). */
function RedModali({
  evrak,
  onClose,
  onRed,
  kaydediliyor,
}: {
  evrak: ContractorDocumentDetail;
  onClose: () => void;
  onRed: (gerekce: string, hassasVeri: boolean) => void;
  kaydediliyor: boolean;
}): React.JSX.Element {
  const [gerekce, setGerekce] = useState('');
  const [hassas, setHassas] = useState(false);
  return (
    <Modal opened onClose={onClose} title={`Reddet — ${evrak.evrakTipiAd}`}>
      <Stack>
        <Textarea
          label="Red gerekçesi"
          data-testid="evrak-red-gerekce"
          value={gerekce}
          onChange={(e) => setGerekce(e.currentTarget.value)}
          minRows={3}
          required
        />
        <Checkbox
          label="Hassas veri içeriyor — dosyanın baytı kalıcı silinsin, kayıt kalsın"
          data-testid="evrak-red-hassas"
          checked={hassas}
          onChange={(e) => setHassas(e.currentTarget.checked)}
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Vazgeç
          </Button>
          <Button
            color={ANLAM.engel}
            data-testid="evrak-red-onayla"
            disabled={gerekce.trim().length === 0}
            loading={kaydediliyor}
            onClick={() => onRed(gerekce.trim(), hassas)}
          >
            Reddet
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
